import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  Button,
  LGBDatepicker,
  AddressSearchInput,
  AddressSearchResult,
  InfoMessageAtom,
} from "@components";
import { Label } from "flowbite-react";
import { HiOutlinePlus } from "react-icons/hi";
import { useWorkspaceData } from "@data";
import { fieldFullWidth, formFooter } from "@design-system";

// Legg til denne nye komponenten rett før RouteSelection-komponenten
interface ContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, phone: string) => void;
  t: any; // Translation function
}

// Kontaktperson form-komponent
const ContactPersonForm: React.FC<ContactDialogProps> = (props) => {
  const { t } = props;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  // Reset feltene når dialogen lukkes
  useEffect(() => {
    if (!props.isOpen) {
      setName("");
      setPhone("");
    }
  }, [props.isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name && phone) {
      props.onAdd(name, phone);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="contact-name" value={t("create.contact_info.name")} />
        <input
          id="contact-name"
          name="contact-name"
          placeholder={t("create.contact_info.name")}
          required
          type="text"
          className={`${fieldFullWidth} mt-2`}
          onChange={(e) => setName(e.target.value)}
          value={name}
          autoComplete="off"
          autoFocus
        />
      </div>

      <div>
        <Label
          htmlFor="contact-phone"
          value={t("create.contact_info.phone_number")}
        />
        <input
          id="contact-phone"
          name="contact-phone"
          placeholder={t("create.contact_info.enter_phone_number")}
          required
          type="text"
          className={`${fieldFullWidth} mt-2`}
          onChange={(e) => setPhone(e.target.value)}
          value={phone}
          autoComplete="off"
        />
      </div>

      <div className="pt-4 flex justify-end gap-4">
        <Button color="gray" onClick={props.onClose}>
          {t("common:close")}
        </Button>
        <Button type="submit" color="primary" disabled={!name || !phone}>
          <HiOutlinePlus className="mr-2 h-5 w-5 dark:text-white" />
          {t("create.contact_info.title")}
        </Button>
      </div>
    </form>
  );
};

// Hovedkomponenten for kontaktperson-dialogen
export const ContactPersonDialog: React.FC<ContactDialogProps> = (props) => {
  return (
    <Dialog
      title={props.t("create.contact_info.title")}
      show={props.isOpen}
      toggleModal={props.onClose}
      hideButtons={true}
      content={<ContactPersonForm {...props} />}
    />
  );
};

// Legg til denne nye komponenten for "Legg til tomt stopp" dialogen
interface AddEmptyStopDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (
    name: string,
    address: string,
    postCode: string,
    city: string,
    country: string,
    description: string,
    isPickup: boolean,
    date: Date,
    timing: string,
    shouldSave: boolean,
  ) => void;
  t: any; // Translation function
  generateTimeOptions: () => string[];
  defaultIsPickup?: boolean;
  shouldSaveLocation?: boolean;
  onSaveToggleChange?: (shouldSave: boolean) => void;
}

// Legg til denne nye komponenten for en toggle-knapp som ikke forårsaker re-rendering
interface StableToggleProps {
  id: string;
  defaultChecked: boolean;
  labelOn: string;
  labelOff: string;
  onChange: (isChecked: boolean) => void;
}

const StableToggle: React.FC<StableToggleProps> = ({
  id,
  defaultChecked,
  labelOn,
  labelOff,
  onChange,
}) => {
  const toggleRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);

  // Initialiser toggle-tilstanden
  useEffect(() => {
    if (toggleRef.current) {
      toggleRef.current.checked = defaultChecked;
    }
    if (labelRef.current) {
      labelRef.current.textContent = defaultChecked ? labelOn : labelOff;
    }
  }, [defaultChecked, labelOn, labelOff]);

  // Håndter endringer uten å bruke React state
  const handleChange = () => {
    const isChecked = toggleRef.current?.checked ?? defaultChecked;

    // Oppdater label-teksten direkte
    if (labelRef.current) {
      labelRef.current.textContent = isChecked ? labelOn : labelOff;
    }

    // Kall onChange-callback
    onChange(isChecked);
  };

  return (
    <div className="flex items-center py-3">
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          ref={toggleRef}
          id={id}
          type="checkbox"
          defaultChecked={defaultChecked}
          className="sr-only peer"
          onChange={handleChange}
        />
        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-gradient-to-r peer-checked:from-blue-400 peer-checked:to-lgb-blue-300"></div>
        <span
          ref={labelRef}
          className="ml-4 text-sm font-medium text-gray-900 dark:text-gray-300"
        >
          {defaultChecked ? labelOn : labelOff}
        </span>
      </label>
    </div>
  );
};

// Form-komponent for "Legg til tomt stopp"
const AddEmptyStopForm: React.FC<AddEmptyStopDialogProps> = (props) => {
  const { t } = props;
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [postCode, setPostCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("NO");
  const [description, setDescription] = useState("");
  const [isPickup, setIsPickup] = useState(props.defaultIsPickup ?? false);
  const [date, setDate] = useState(new Date());
  const [timing, setTiming] = useState("4"); // 4 = earliest
  const [isAddressValid, setIsAddressValid] = useState(false);

  // Eksisterende lokasjoner (for duplicate detection) fra det sentrale datalageret.
  const { locations: existingLocations } = useWorkspaceData();

  // Duplicate detection
  const duplicateLocation = useMemo(() => {
    if (!address || !postCode || !city) return null;

    return (
      existingLocations.find(
        (loc) =>
          loc.addressLine === address &&
          loc.postCode === postCode &&
          loc.city === city,
      ) || null
    );
  }, [address, postCode, city, existingLocations]);

  // Handle address selection from AddressSearchInput
  const handleAddressSelect = (result: AddressSearchResult) => {
    setAddress(result.addressLine);
    setPostCode(result.postCode);
    setCity(result.city);
    setCountry(result.countryCode || "NO");

    // Auto-fill display name from address
    if (!name) {
      setName(result.addressLine);
    }
  };

  const handleAddressValidated = (isValid: boolean) => {
    setIsAddressValid(isValid);
  };

  // Bruk en ref for å holde toggle-tilstanden i stedet for state
  const saveLocationRef = useRef<boolean>(props.shouldSaveLocation ?? true);

  // Reset feltene når dialogen lukkes
  useEffect(() => {
    if (!props.isOpen) {
      setName("");
      setAddress("");
      setPostCode("");
      setCity("");
      setCountry("NO");
      setDescription("");
      setIsPickup(props.defaultIsPickup ?? false);
      setDate(new Date());
      setTiming("4");
      setIsAddressValid(false);
    } else {
      // Sett standard verdier når dialogen åpnes
      setIsPickup(props.defaultIsPickup ?? false);
      saveLocationRef.current = props.shouldSaveLocation ?? true;
    }
  }, [props.isOpen, props.defaultIsPickup, props.shouldSaveLocation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent submission if address is not valid
    if (!isAddressValid) {
      return;
    }

    if (postCode && city) {
      // Hent toggle-status direkte fra ref
      const shouldSave = saveLocationRef.current;
      props.onAdd(
        name,
        address,
        postCode,
        city,
        country,
        description,
        isPickup,
        date,
        timing,
        shouldSave,
      );
    }
  };

  // Funksjon for å håndtere toggle-endringer
  const handleToggleChange = (isChecked: boolean) => {
    saveLocationRef.current = isChecked;

    // Informer parent-komponenten om endringen
    if (props.onSaveToggleChange) {
      props.onSaveToggleChange(isChecked);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Adresse øverst */}
      <div>
        <Label htmlFor="newLocationAddress" value={t("common:address_line")} />
        <div className="space-y-2">
          <AddressSearchInput
            id="newLocationAddress"
            name="newLocationAddress"
            placeholder={t("common:placeholder.address_line")}
            value={address}
            onChange={setAddress}
            onSelectAddress={handleAddressSelect}
            onAddressValidated={handleAddressValidated}
            required={true}
            autoFocus
          />
          {duplicateLocation && (
            <InfoMessageAtom
              variant="warning"
              title={t("location:duplicate_warning_title", {
                defaultValue: "Location already exists",
              })}
            >
              {t("location:duplicate_warning_message", {
                defaultValue:
                  "{{name}} has the same address. We recommend re-using the same location",
                name: duplicateLocation.displayName,
              })}
            </InfoMessageAtom>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="newLocationPostCode" value={t("common:postcode")} />
          <input
            id="newLocationPostCode"
            type="text"
            className={`${fieldFullWidth} mt-2`}
            placeholder={t("common:placeholder.postcode")}
            value={postCode}
            onChange={(e) => setPostCode(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        <div>
          <Label htmlFor="newLocationCity" value={t("common:city")} />
          <input
            id="newLocationCity"
            type="text"
            className={`${fieldFullWidth} mt-2`}
            placeholder={t("common:placeholder.city")}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
      </div>

      {/* Visningsnavn under postcode og city */}
      <div>
        <Label
          htmlFor="newLocationName"
          value={t("location:modal.display_name")}
        />
        <input
          id="newLocationName"
          type="text"
          className={`${fieldFullWidth} mt-2`}
          placeholder={t("location:modal.display_name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="new-password"
          required
        />
      </div>

      <div>
        <Label htmlFor="newLocationCountry" value={t("common:country")} />
        <select
          id="newLocationCountry"
          className={`${fieldFullWidth} mt-2`}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          autoComplete="new-password"
        >
          <option value="NO">Norge</option>
          <option value="SE">Sverige</option>
          <option value="DK">Danmark</option>
          <option value="FI">Finland</option>
        </select>
      </div>

      <div>
        <Label
          htmlFor="newLocationDescription"
          value={t("common:description")}
        />
        <textarea
          id="newLocationDescription"
          rows={2}
          className={`${fieldFullWidth} mt-2`}
          placeholder={t("common:placeholder.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      {/* Pickup/Delivery toggle */}
      <div className="pt-4 border-t border-border-subtle dark:border-[#2c3353]">
        <Label
          htmlFor="newStopType"
          value={t("create.route_selection.stop_type")}
        />
        <div className="flex space-x-4 mt-2">
          <div className="flex items-center">
            <input
              id="newPickup"
              type="radio"
              name="newStopType"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-[#2c3353]"
              checked={isPickup === true}
              onChange={() => setIsPickup(true)}
            />
            <label
              htmlFor="newPickup"
              className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
            >
              {t("pickup")}
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="newDelivery"
              type="radio"
              name="newStopType"
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-[#2c3353]"
              checked={isPickup === false}
              onChange={() => setIsPickup(false)}
            />
            <label
              htmlFor="newDelivery"
              className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
            >
              {t("delivery")}
            </label>
          </div>
        </div>
      </div>

      {/* Date picker */}
      <div>
        <Label htmlFor="newDate" value={t("create.route_selection.date")} />
        <LGBDatepicker
          inputId="newDate"
          selectedDate={date}
          select={(newDate) => setDate(newDate)}
        />
      </div>

      {/* Timing selector */}
      <div>
        <Label htmlFor="newTiming" value={t("create.route_selection.timing")} />
        <select
          id="newTiming"
          className={`${fieldFullWidth} mt-2`}
          value={timing}
          onChange={(e) => setTiming(e.target.value)}
        >
          <option value="1">{t("create.route_selection.before")}</option>
          <option value="2">{t("create.route_selection.between")}</option>
          <option value="3">{t("create.route_selection.after")}</option>
          <option value="4">
            {t("create.route_selection.earliest_possible")}
          </option>
          <option value="5">
            {t("create.route_selection.latest_possible")}
          </option>
        </select>
      </div>

      {/* Legg til toggle for å lagre lokasjonen */}
      <div className={`${formFooter} mt-4`}>
        <StableToggle
          id="save-location-toggle"
          defaultChecked={props.shouldSaveLocation ?? true}
          labelOn={t("create.route_selection.saved")}
          labelOff={t("create.route_selection.not_saved")}
          onChange={handleToggleChange}
        />
      </div>

      {/* Footer */}
      <div className="pt-4 flex justify-end gap-4">
        <Button color="gray" onClick={props.onClose}>
          {t("common:cancel")}
        </Button>
        <Button type="submit" color="primary" disabled={!postCode || !city}>
          <HiOutlinePlus className="mr-2 h-5 w-5 dark:text-white" />
          {t("create.route_selection.add_stop")}
        </Button>
      </div>
    </form>
  );
};

// Hovedkomponenten for "Legg til tomt stopp" dialogen
export const AddEmptyStopDialog: React.FC<AddEmptyStopDialogProps> = (props) => {
  return (
    <Dialog
      title={props.t("create.route_selection.add_stop")}
      show={props.isOpen}
      toggleModal={props.onClose}
      hideButtons={true}
      content={<AddEmptyStopForm {...props} />}
    />
  );
};
